/**
 * RoleFilterDropdown - Role filter built on the generic FilterDropdown.
 *
 * Maps CARD_ROLE_ORDER into FilterDropdown options with role-colored icons.
 */
import type { ReactElement } from 'react';

import {
  GiCardDraw,
  GiCheckedShield,
  GiCrystalGrowth,
  GiDeathSkull,
  GiFeatheredWing,
  GiFireBomb,
  GiMagnifyingGlass,
  GiRainbowStar,
  GiRecycle,
} from 'react-icons/gi';

import type { FilterDropdownOption } from '@/components/FilterDropdown/FilterDropdown';
import FilterDropdown from '@/components/FilterDropdown/FilterDropdown';
import { CARD_ROLE_INFO, CARD_ROLE_ORDER } from '@/lib/cardClassification';
import type { CardRoleId } from '@/types/card';

// ============================================================================
// Types
// ============================================================================

export interface RoleFilterDropdownProps {
  selectedRoles: string[];
  onToggle: (roleId: string) => void;
  onClear: () => void;
  className?: string;
}

// ============================================================================
// Static data (built once from CARD_ROLE_ORDER + CARD_ROLE_INFO)
// ============================================================================

const ROLE_ICONS: Record<CardRoleId, typeof GiCrystalGrowth> = {
  ramp: GiCrystalGrowth,
  cardDraw: GiCardDraw,
  removal: GiDeathSkull,
  boardWipe: GiFireBomb,
  protection: GiCheckedShield,
  recursion: GiRecycle,
  manaFixing: GiRainbowStar,
  tutor: GiMagnifyingGlass,
  evasion: GiFeatheredWing,
};

const ROLE_OPTIONS: FilterDropdownOption[] = CARD_ROLE_ORDER.map((roleId) => {
  const info = CARD_ROLE_INFO[roleId];
  const Icon = ROLE_ICONS[roleId];
  return {
    value: roleId,
    label: info.label,
    icon: <Icon className={`h-4 w-4 ${info.color}`} aria-hidden="true" />,
    badgeClassName: `${info.bgColor} ${info.color}`,
  };
});

const TRIGGER_ICON = <GiCrystalGrowth className="h-3.5 w-3.5" aria-hidden="true" />;

// ============================================================================
// Main Component
// ============================================================================

export default function RoleFilterDropdown({ selectedRoles, onToggle, onClear, className }: RoleFilterDropdownProps): ReactElement {
  return (
    <FilterDropdown
      triggerLabel="Roles"
      triggerIcon={TRIGGER_ICON}
      ariaLabel="Role filter"
      searchPlaceholder="Search roles..."
      options={ROLE_OPTIONS}
      selectedValues={selectedRoles}
      onToggle={onToggle}
      onClear={onClear}
      className={className}
    />
  );
}
